import classNames from "classnames"
import ApplicationComponent from "legacy/ApplicationComponent"
import {
  badgeForReservationStatus,
  boatDisplayText,
  capitalizeFirstLetter,
  humanize,
  inchesToFeet,
  isPresent,
  metaStatus,
} from "legacy/helpers.es6"
import _ from "lodash"
import moment from "moment"
import PropTypes from "prop-types"
import React from "react"

class AssignmentsPopover extends ApplicationComponent {
  constructor(props) {
    super(props)
    this.state = {}
    this.setMousePosition = this.setMousePosition.bind(this)
  }

  setMousePosition(event) {
    this.setState({ x: event.clientX, y: event.clientY })
  }

  componentDidMount() {
    window.addEventListener("mousemove", this.setMousePosition)
  }

  componentWillUnmount() {
    window.removeEventListener("mousemove", this.setMousePosition)
  }

  determineTieSide(data) {
    return data.bow_location > data.stern_location ? "Port" : "Starboard"
  }

  renderAssignment(assignment, showWarning = false) {
    return (
      <div>
        <div
          className={classNames("bg-gray w-full border-b p-4 text-left", {
            "border-t": this.props.data.data.spaceGroup,
            "rounded-t": !this.props.data.data.spaceGroup,
          })}
        >
          {showWarning ? (
            <i className="icon icon-warning text-yellow mr-2" />
          ) : null}
          <span className="font-bold">{assignment.contact_name}</span> -{" "}
          {boatDisplayText(
            assignment.boat_name,
            inchesToFeet(assignment.length_overall),
            assignment.boat_type
          )}
        </div>
        <div className="p-4">
          <div className="row form-row">
            <div className="col-sm-6 mb-2">
              <div className="font-bold">Arrival</div>
              {moment(assignment.check_in_date, "YYYY-MM-DD").format(
                "ddd MM/DD/YYYY"
              )}
            </div>
            <div className="col-sm-6 mb-2">
              <div className="font-bold">Departure</div>
              {moment(assignment.check_out_date, "YYYY-MM-DD").format(
                "ddd MM/DD/YYYY"
              )}
            </div>
          </div>
          <div className="row form-row">
            <div className="col-sm-6 mb-2">
              <div className="font-bold">Status</div>
              <div
                className={classNames(
                  `badge rounded-sm text-sm ${badgeForReservationStatus(
                    metaStatus(
                      assignment.reservation_status,
                      assignment.checked_in_at,
                      assignment.checked_out_at
                    ),
                    assignment.reservationLabel
                  )}`,
                  { "text-gray-900": assignment.reservationLabel }
                )}
              >
                {humanize(
                  metaStatus(
                    assignment.reservation_status,
                    assignment.checked_in_at,
                    assignment.checked_out_at
                  )
                )}
              </div>
            </div>
            {isPresent(assignment.reservationLabel) ? (
              <div className="col-sm-6 mb-2">
                <div className="font-bold">Label</div>
                <div className="mt-1 flex flex-row">
                  <div
                    className={`mr-1 mt-0.5 h-4 w-4 rounded-full text-center rl-bg-${assignment.reservationLabel.color}-dark`}
                  />
                  <p>{assignment.reservationLabel.label}</p>
                </div>
              </div>
            ) : null}
          </div>
          {isPresent(assignment.bow_location) &&
            isPresent(assignment.stern_location) && (
              <div className="row form-row">
                <div className="col-sm-6 mb-2">
                  <div className="font-bold">Assignment Location</div>
                  {assignment.bow_location > assignment.stern_location ? (
                    <div>
                      {inchesToFeet(assignment.stern_location)}&apos; -{" "}
                      {inchesToFeet(assignment.bow_location)}&apos;
                    </div>
                  ) : (
                    <div>
                      {inchesToFeet(assignment.bow_location)}&apos; -{" "}
                      {inchesToFeet(assignment.stern_location)}&apos;
                    </div>
                  )}
                </div>
                <div className="col-sm-6 mb-2">
                  <div className="font-bold">Tie</div>
                  <div>{this.determineTieSide(assignment)}</div>
                </div>
              </div>
            )}
          {this.assignmentNotes(assignment)}
        </div>
      </div>
    )
  }

  renderPlaceholder(data) {
    return (
      <div className="p-4">
        <div className="row form-row">
          <div className="col-sm-6">
            <div className="font-bold">Start</div>
            {moment(data.start_date, "YYYY-MM-DD").format("ddd MM/DD/YYYY")}
          </div>
          <div className="col-sm-6">
            <div className="font-bold">End</div>
            {moment(data.end_date, "YYYY-MM-DD").format("ddd MM/DD/YYYY")}
          </div>
          {isPresent(data.length_overall) && (
            <div className="col-sm-6">
              <div className="font-bold">LOA</div>
              <div>{data.length_overall}&apos;</div>
            </div>
          )}
        </div>
        <hr className="my-3" />
        <div>{data.note}</div>
      </div>
    )
  }

  renderData() {
    const data = this.props.data.data.objects
    const showWarning = data.length > 1
    // if the data array contains more than one object, it will render a popover with multiple assignment/placeholder objects
    if (showWarning) {
      return (
        <div>
          {this.renderSpaceGroupInfo()}
          {_.map(data, (obj) => {
            if (obj.type === "assignment") {
              return (
                <div key={obj.reservation_id}>
                  {this.renderAssignment(obj, showWarning)}
                </div>
              )
            } else {
              return (
                <div key={obj.id}>
                  <div className="bg-gray w-full border-b border-t p-4 text-left font-bold">
                    {showWarning ? (
                      <i className="icon icon-warning text-yellow mr-2" />
                    ) : null}
                    {/* When we implement space notes on the map, we can replace null in the below ternary statement with the note icon */}
                    {obj.type === "placeholder" ? (
                      <i className="icon icon-thumb-tack mr-2 mt-1" />
                    ) : null}
                    {capitalizeFirstLetter(obj.type)}
                  </div>
                  {this.renderPlaceholder(obj)}
                </div>
              )
            }
          })}
        </div>
      )
    } else {
      return _.map(data, (obj) => {
        if (obj.type === "assignment") {
          return (
            <div key={obj.reservation_id}>
              {this.renderSpaceGroupInfo()}
              {this.renderAssignment(obj)}
            </div>
          )
        } else {
          return <div key={obj.id}>{this.renderPlaceholder(obj)}</div>
        }
      })
    }
  }

  renderSpaceGroupData(data) {
    if (data.type === "SpaceGroup::LinearV2") {
      if (data.orientation_start && data.orientation_end) {
        return (
          <div className="px-4 py-2">
            <div className="row form-row">
              <div className="col-sm-6 mb-2 font-bold">Orientation</div>
            </div>
            <div className="row form-row">
              <div className="col-sm-8 mb-2">
                {data.orientation_start} (0&apos;) → {data.orientation_end} (
                {inchesToFeet(data.total)}&apos;)
              </div>
            </div>
          </div>
        )
      }
    } else {
      if (!_.isNull(data.min_beam) && !_.isNull(data.max_beam)) {
        return (
          <div className="px-4 py-2">
            <div className="row form-row">
              <div className="col-sm-6 mb-2 font-bold">Beam</div>
            </div>
            <div className="row form-row">
              <div className="col-sm-6 mb-2">
                {inchesToFeet(data.min_beam)}&apos; -{" "}
                {inchesToFeet(data.max_beam)}&apos;
              </div>
            </div>
          </div>
        )
      }
    }
  }

  renderSpaceGroupInfo() {
    if (this.props.data.data.spaceGroup) {
      const data = this.props.data.data.spaceGroup
      return (
        <div>
          <div className="w-full rounded-t bg-blue-900 p-4 text-left text-white">
            <span className="font-bold">{data.name}</span>
          </div>
          {this.renderSpaceGroupData(data)}
        </div>
      )
    }
  }

  assignmentNotes(data) {
    const html = []
    if (data.special_request) {
      html.push(
        <div key="special-request">
          <hr className="my-3" />
          <div className="font-bold">Special Request</div>
          <div>{data.special_request}</div>
        </div>
      )
    }
    if (data.reservation_note) {
      html.push(
        <div key="reservation-note">
          <hr className="my-3" />
          <div className="font-bold">Reservation Note</div>
          <div>{data.reservation_note}</div>
        </div>
      )
    }
    if (data.contact_note) {
      html.push(
        <div key="customer-note">
          <hr className="my-3" />
          <div className="font-bold">Customer Note</div>
          <div>{data.contact_note}</div>
        </div>
      )
    }
    return html
  }

  render() {
    if (!this.props.data) return <div />

    const width = 350
    const estimatedHeight = 380
    const style = { zIndex: 1000, width, fontSize: 14 }

    if (this.state.x + width > window.innerWidth) {
      // Place the popover to the left of the cursor
      style.right = `${window.innerWidth - this.state.x + 5}px`
    } else {
      // Place the popover to the right of the cursor
      style.left = `${this.state.x + 5}px`
    }

    if (this.state.y + estimatedHeight > window.innerHeight) {
      // Place the popover to above the cursor
      style.bottom = `${window.innerHeight - this.state.y}px`
    } else {
      // Place the popover to below the cursor
      style.top = `${this.state.y + 15}px`
    }

    return (
      <div
        id="assignments-popover"
        className="fixed rounded border bg-white shadow-lg"
        style={style}
      >
        {this.renderData()}
      </div>
    )
  }
}

AssignmentsPopover.propTypes = {
  data: PropTypes.object,
}

export default AssignmentsPopover
